/**
 * Adaptive Learning API Client
 * Frontend client for consuming Adaptive Learning API endpoints
 * 
 * Usage:
 * const api = new AdaptiveAPIClient(baseURL);
 * const session = await api.startSession(userId, courseId);
 */
export class AdaptiveAPIClient {
  constructor(baseURL = '/api/v1/adaptive') {
    this.baseURL = AdaptiveAPIClient.normalizeBaseURL(baseURL);
    this.headers = {
      'Content-Type': 'application/json',
    };
  }

  static normalizeBaseURL(value) {
    if (!value) return '/api/v1/adaptive';

    let normalized = String(value).trim();
    if (!normalized) return '/api/v1/adaptive';

    const isVercelFrontend = typeof window !== 'undefined'
      && /(?:^|\.)vercel\.app$/i.test(window.location.hostname || '');

    if (/^api(\/|$)/i.test(normalized)) {
      normalized = `/${normalized}`;
    }

    if (normalized.startsWith('/')) {
      normalized = normalized.replace(/\/$/, '');
      return normalized.endsWith('/api/v1/adaptive')
        ? normalized
        : `${normalized}/api/v1/adaptive`;
    }

    if (!/^https?:\/\//i.test(normalized)) {
      normalized = `https://${normalized}`;
    }

    try {
      const parsed = new URL(normalized);
      if (isVercelFrontend) {
        return '/api/v1/adaptive';
      }

      const isLocalHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
      if (parsed.protocol === 'http:' && !isLocalHost) {
        parsed.protocol = 'https:';
      }

      const root = parsed.origin.replace(/\/$/, '');
      const path = parsed.pathname.replace(/\/$/, '');
      const apiPath = path.endsWith('/api/v1/adaptive') ? path : `${path}/api/v1/adaptive`;
      return `${root}${apiPath}`.replace(/\/\/+/, '/').replace(':/', '://');
    } catch {
      return '/api/v1/adaptive';
    }
  }

  /**
   * Make HTTP request
   */
  async _request(method, endpoint, data = null) {
    const url = `${this.baseURL}${endpoint}`;
    const options = {
      method,
      headers: this.headers,
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`[AdaptiveAPIClient] ${method} ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Start or resume adaptive session
   * POST /start
   */
  async startSession(userId, courseId, resumeIfActive = true) {
    return this._request('POST', '/start', {
      userId,
      courseId,
      resumeIfActive,
    });
  }

  /**
   * Get next adaptive step
   * GET /next
   */
  async getNextStep(userId, courseId, sectionId, sessionId) {
    const params = new URLSearchParams({
      userId,
      courseId,
      sectionId,
      sessionId,
    });

    const endpoint = `/next?${params.toString()}`;
    return this._request('GET', endpoint);
  }

  /**
   * Submit answer
   * POST /submit
   */
  async submitAnswer(answerData) {
    const {
      userId,
      courseId,
      sessionId,
      questionId,
      sectionId,
      userAnswer,
      isCorrect,
      tagIds = [],
      hintsUsed = 0,
      timeSpent = 0,
      fiftyFiftyUsed = false,
      isLastQuestion = false,
      reviewContentUsed = false,
      selectedAnswer = null,
      skipped = false,
    } = answerData;

    if (!userId || !courseId || !sessionId || !questionId || !sectionId) {
      throw new Error('Missing required answer fields');
    }

    return this._request('POST', '/submit', {
      userId,
      courseId,
      sessionId,
      questionId,
      sectionId,
      userAnswer,
      isCorrect,
      tagIds,
      hintsUsed,
      timeSpent,
      fiftyFiftyUsed,
      isLastQuestion,
      reviewContentUsed,
      selectedAnswer,
      skipped,
    });
  }

  /**
   * Get session summary
   * GET /session-summary
   */
  async getSessionSummary(userId, courseId, sessionId) {
    const params = new URLSearchParams({
      userId,
      courseId,
      sessionId,
    });

    const endpoint = `/session-summary?${params.toString()}`;
    return this._request('GET', endpoint);
  }

  /**
   * Complete session
   * POST /complete
   */
  async completeSession(userId, courseId, sessionId) {
    return this._request('POST', '/complete', {
      userId,
      courseId,
      sessionId,
    });
  }

  /**
   * Set authorization token (if needed)
   */
  setAuthToken(token) {
    this.headers['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove authorization token
   */
  clearAuthToken() {
    delete this.headers['Authorization'];
  }
}

// Export singleton instance using default API URL
export const adaptiveAPIClient = new AdaptiveAPIClient();

export default AdaptiveAPIClient;
